import DeleteIcon from "@mui/icons-material/Delete";
import {
  Box,
  IconButton,
  MenuItem,
  TextField,
  type SxProps,
  type Theme,
} from "@mui/material";
import { memo, useCallback, type ChangeEvent } from "react";
import { PackageType } from "../../../generated/graphql";
import { type PackageFormState } from "../api-order-form";

const styles = {
  packageRow: {
    display: "grid",
    gridTemplateColumns: "2fr 1fr 1fr 1fr 2fr auto",
    gap: 2,
    alignItems: "center",
  } satisfies SxProps<Theme>,
};

const PACKAGE_TYPES = Object.values(PackageType);

type ApiOrderPackageRowProps = {
  readonly packageForm: PackageFormState;
  /** Set on the last remaining row, which the API requires the order to keep. */
  readonly removeDisabled: boolean;
  readonly onChange: (next: PackageFormState) => void;
  readonly onRemove: (packageId: string) => void;
};

/** One package of the order. */
const ApiOrderPackageRow = memo(
  ({
    packageForm,
    removeDisabled,
    onChange,
    onRemove,
  }: ApiOrderPackageRowProps) => {
    const handleDescriptionChange = useCallback(
      (event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        onChange({ ...packageForm, description: event.target.value });
      },
      [onChange, packageForm],
    );

    const handleQuantityChange = useCallback(
      (event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        onChange({ ...packageForm, quantity: event.target.value });
      },
      [onChange, packageForm],
    );

    const handleWeightChange = useCallback(
      (event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        onChange({ ...packageForm, weight: event.target.value });
      },
      [onChange, packageForm],
    );

    const handleTypeChange = useCallback(
      (event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        onChange({
          ...packageForm,
          type: event.target.value as PackageFormState["type"],
        });
      },
      [onChange, packageForm],
    );

    const handleBarcodesChange = useCallback(
      (event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        onChange({ ...packageForm, barcodes: event.target.value });
      },
      [onChange, packageForm],
    );

    const handleRemove = useCallback(() => {
      onRemove(packageForm.id);
    }, [onRemove, packageForm.id]);

    return (
      <Box sx={styles.packageRow}>
        <TextField
          required
          label="Description"
          size="small"
          value={packageForm.description}
          onChange={handleDescriptionChange}
        />
        <TextField
          label="Quantity"
          size="small"
          type="number"
          value={packageForm.quantity}
          onChange={handleQuantityChange}
        />
        <TextField
          label="Weight"
          size="small"
          type="number"
          value={packageForm.weight}
          onChange={handleWeightChange}
        />
        <TextField
          select
          label="Type"
          size="small"
          value={packageForm.type}
          onChange={handleTypeChange}
        >
          <MenuItem value="">Unset</MenuItem>
          {PACKAGE_TYPES.map((packageType) => (
            <MenuItem key={packageType} value={packageType}>
              {packageType}
            </MenuItem>
          ))}
        </TextField>
        <TextField
          helperText="Comma separated; the first becomes the secondary reference number."
          label="Barcodes"
          size="small"
          value={packageForm.barcodes}
          onChange={handleBarcodesChange}
        />
        <IconButton
          aria-label="Remove package"
          disabled={removeDisabled}
          onClick={handleRemove}
        >
          <DeleteIcon />
        </IconButton>
      </Box>
    );
  },
);

ApiOrderPackageRow.displayName = "ApiOrderPackageRow";

export default ApiOrderPackageRow;
